<script setup lang="ts">
import { asText } from '@prismicio/client'
import type { Content } from "@prismicio/client";

defineProps(getSliceComponentProps<Content.BranchSliderSlice>());

const { client } = usePrismic()
const { data: branches } = await useAsyncData('branches', () =>
  client.getAllByType('branch', {
    orderings: [{ field: 'my.branch.order' }],
  })
)

const wrapperEl = ref<HTMLElement | null>(null)
const pinEl = ref<HTMLElement | null>(null)
const trackEl = ref<HTMLElement | null>(null)
const wrapperHeight = ref<string | null>(null)
const translateX = ref(0)
const progress = ref(0)

let raf = 0
let horizontalDistance = 0

function isDesktop() {
  return window.matchMedia('(min-width: 768px)').matches
}

function calc() {
  if (!wrapperEl.value || !trackEl.value || !pinEl.value) return
  if (!isDesktop()) {
    wrapperHeight.value = null
    translateX.value = 0
    horizontalDistance = 0
    return
  }
  horizontalDistance = Math.max(0, trackEl.value.scrollWidth - window.innerWidth)
  const pinHeight = pinEl.value.offsetHeight
  wrapperHeight.value = horizontalDistance > 0
    ? `${pinHeight + horizontalDistance}px`
    : null
}

function updateScroll() {
  if (!wrapperEl.value || !isDesktop() || horizontalDistance === 0) {
    translateX.value = 0
    progress.value = 0
    return
  }
  cancelAnimationFrame(raf)
  raf = requestAnimationFrame(() => {
    const rect = wrapperEl.value!.getBoundingClientRect()
    const scrolled = Math.max(0, Math.min(-rect.top, horizontalDistance))
    translateX.value = -scrolled
    progress.value = scrolled / horizontalDistance
  })
}

function onResize() {
  calc()
  updateScroll()
}

onMounted(async () => {
  await nextTick()
  calc()
  updateScroll()
  window.addEventListener('scroll', updateScroll, { passive: true })
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateScroll)
  window.removeEventListener('resize', onResize)
  cancelAnimationFrame(raf)
})
</script>

<template>
  <section
      ref="wrapperEl"
      :data-slice-type="slice.slice_type"
      :data-slice-variation="slice.variation"
      class="pa-branch-slider pa-bounded"
      :style="wrapperHeight ? { height: wrapperHeight } : {}"
  >
    <div ref="pinEl" class="pa-branch-slider__pin">
      <div class="pa-container">
        <div class="pa-branch-slider__header">
          <div>
            <div v-if="slice.primary.eyebrow" class="pa-branch-slider__eyebrow">
              {{ slice.primary.eyebrow }}
            </div>
            <div class="pa-branch-slider__heading">
              <PrismicRichText :field="slice.primary.heading" />
            </div>
          </div>
          <TrackedPrismicLink
              v-if="slice.primary.cta_link && ('id' in slice.primary.cta_link || 'url' in slice.primary.cta_link)"
              :field="slice.primary.cta_link"
              :track="slice.primary.cta_track_event"
              class="pa-branch-slider__cta"
          >
            {{ slice.primary.cta_link.text || "Bekijk branches" }}
            <span aria-hidden="true">&rarr;</span>
          </TrackedPrismicLink>
        </div>
      </div>

      <div class="pa-branch-slider__viewport">
        <div
            ref="trackEl"
            class="pa-branch-slider__track"
            :style="{ transform: `translate3d(${translateX}px, 0, 0)` }"
        >
          <NuxtLink
              v-for="branch in branches"
              :key="branch.id"
              :to="branch.url || `/sectoren/${branch.uid}`"
              class="pa-branch-slider__card"
          >
            <div class="pa-branch-slider__card-content">
              <h3 class="pa-branch-slider__card-title">
                {{ asText(branch.data.title) }}
              </h3>
              <hr class="pa-branch-slider__card-divider" />
              <span class="pa-branch-slider__card-arrow" aria-hidden="true">&rarr;</span>
            </div>
            <div class="pa-branch-slider__card-image">
              <PrismicImage
                  v-if="branch.data.featured_image?.url"
                  :field="branch.data.featured_image"
                  :widths="[400, 600, 800, 1200]"
                  sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 90vw"
                  :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
              />
            </div>
          </NuxtLink>
        </div>
      </div>

      <div class="pa-container">
        <div class="pa-branch-slider__progress">
          <div
              class="pa-branch-slider__progress-bar"
              :style="{ width: progress * 100 + '%' }"
          />
        </div>
      </div>
    </div>
  </section>
</template>
